import clsx from 'clsx';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';

interface WrapperProps extends PropsWithChildren {
  variant?: 'default' | 'left';
}
/**
 * Wrapper component for rendering content with different layout variants.
 *
 * @param {Object} props - The props for the Wrapper component.
 * @param {'default' | 'left'} variant - The layout variant to use.
 * - `default`: Standard layout.
 * - `left`: Content aligned to the left (DESKTOP ONLY - mobile is centered)
 * @returns {JSX.Element} The rendered Wrapper component.
 */
export const Wrapper: FC<WrapperProps> = ({
  variant = 'default',
  children,
}) => {
  const getMarginWindowWidth = useCallback(() => {
    if (typeof window !== 'undefined' && variant !== 'default') {
      const windowWidth = window.innerWidth;
      if (windowWidth > 1920) {
        const margin = (windowWidth - 1920) / 2;
        return `${1920 + margin}px`;
      }
    }
    return '1920px';
  }, [variant]);

  const [width, setWidth] = useState(getMarginWindowWidth());

  useEffect(() => {
    const handleResize = () => {
      setWidth(getMarginWindowWidth());
    };
    if (variant !== 'default') {
      window.addEventListener('resize', handleResize);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [variant, getMarginWindowWidth]);

  return (
    <section
      className={clsx('flex w-full', {
        'justify-center': variant === 'default',
        'justify-end': variant === 'left',
      })}
    >
      <section
        className={clsx('flex w-full px-l', {
          'ml:px-3xl l:px-[60px] xl:px-[100px]': variant === 'default',
          'ml:pl-3xl ml:pr-0 l:pl-[60px] xl:pl-[100px]': variant === 'left',
        })}
        style={{
          maxWidth: width,
        }}
      >
        {children}
      </section>
    </section>
  );
};
